import React from "react";
import { api, fmtDate, fmtMoney } from "@/lib/caesar";
import { Pill } from "@/components/common";
import { toast } from "sonner";
import { Check, ArrowUpRight, UserPlus, Clock, Pin } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PRIORITY_COLOR = (score) => (score >= 16 ? "#E11D48" : score >= 10 ? "#F59E0B" : "#D4AF37");

export function CaesarFive({ items, projectMap = {}, onReload }) {
  const navigate = useNavigate();

  const complete = async (t) => {
    await api.put(`/tasks/${t.id}`, { status: "done" });
    toast.success("Completed");
    onReload && onReload();
  };
  const delegate = async (t) => {
    const owner = window.prompt("Delegate to (name):", t.owner || "");
    if (owner == null) return;
    await api.put(`/tasks/${t.id}`, { owner });
    toast.success(`Delegated to ${owner}`);
    onReload && onReload();
  };
  const reschedule = async (t) => {
    const d = window.prompt("New deadline (YYYY-MM-DD):", (t.deadline || "").slice(0, 10));
    if (d == null) return;
    await api.put(`/tasks/${t.id}`, { deadline: d ? new Date(d).toISOString() : "" });
    toast.success("Rescheduled");
    onReload && onReload();
  };
  const pin = async (t) => {
    await api.put(`/tasks/${t.id}`, { pinned: !t.pinned });
    onReload && onReload();
  };

  if (!items || items.length === 0) {
    return (
      <div className="border border-dashed border-edge rounded-xl p-6 text-center text-[#A3A3A3]">
        No priority actions yet. Capture something or create a task.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((t, i) => {
        const score = t.score ?? 0;
        return (
          <div key={t.id} data-testid={`caesar-five-item-${i}`} className="bg-surface border border-edge rounded-xl p-4 hover:border-gold/30 transition-colors duration-150">
            <div className="flex items-start gap-3">
              <div className="font-num text-2xl font-bold text-gold/40 w-8 shrink-0">{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-[#FAFAFA] leading-snug">{t.title}</p>
                  <button onClick={() => pin(t)} title="Pin" className={t.pinned ? "text-gold" : "text-[#4b4b4b] hover:text-[#A3A3A3]"}>
                    <Pin size={15} fill={t.pinned ? "#D4AF37" : "none"} />
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {t.project_id && projectMap[t.project_id] && <Pill color="#A3A3A3">{projectMap[t.project_id]}</Pill>}
                  {t.owner && <Pill color="#A3A3A3">{t.owner}</Pill>}
                  {t.deadline && <Pill color="#F59E0B">{fmtDate(t.deadline)}</Pill>}
                  <Pill color={PRIORITY_COLOR(score)} solid>P{Math.round(score)}</Pill>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <ActionBtn testId={`five-complete-${i}`} onClick={() => complete(t)} icon={Check} label="Complete" primary />
                  <ActionBtn testId={`five-open-${i}`} onClick={() => navigate("/today")} icon={ArrowUpRight} label="Open" />
                  <ActionBtn testId={`five-delegate-${i}`} onClick={() => delegate(t)} icon={UserPlus} label="Delegate" />
                  <ActionBtn testId={`five-reschedule-${i}`} onClick={() => reschedule(t)} icon={Clock} label="Reschedule" />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ActionBtn({ onClick, icon: Icon, label, primary, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className={primary
        ? "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gold text-ink text-xs font-bold hover:bg-[#e6c14e] transition-colors duration-150"
        : "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-edge text-xs font-semibold text-[#A3A3A3] hover:text-[#FAFAFA] hover:border-gold/30 transition-colors duration-150"}>
      {Icon && <Icon size={13} />} {label}
    </button>
  );
}

export function ListCard({ title, count, children, testId }) {
  return (
    <div data-testid={testId} className="bg-surface border border-edge rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-edge flex items-center justify-between">
        <span className="label-caps">{title}</span>
        {count != null && <span className="font-num text-sm text-[#A3A3A3]">{count}</span>}
      </div>
      <div className="p-2">{children}</div>
    </div>
  );
}

export function MutedEmpty({ children }) {
  return <div className="px-3 py-5 text-sm text-[#5a5a5a] text-center">{children}</div>;
}
